import React, { useState, useMemo } from 'react';
import * as Yup from 'yup';
import { Formik } from 'formik';
import './transactions-form.scss'
import { toast } from 'react-toastify';
import BeatLoader from "react-spinners/BeatLoader";
import Constant from "../../../Constant";
import { getTransactionInfo, getBalanceInfo } from '../../../services/transaction-service';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Modal from "../../../components/Modal";
import { FaRegEye  } from 'react-icons/fa';
import ViewTxForm  from './tx-view';
import { MdModeEditOutline } from 'react-icons/md';
import Excel from "../../../utils/Excel";
import { TableButton } from "../../../components";

const orderSchema = Yup.object().shape({
  start: Yup.string()
    .required('Start date is required'),
  end: Yup.string()
    .required('End date is required'),
  pin: Yup.string()
    .required('PIN is required')
    .min(5, 'PIN must be exactly 5 characters')
    .max(5, "PIN must be exactly 5 characters")
    .matches(
      /^([0-9]){0,5}/,
      'PIN should be numeric. No special characters allowed'
    ),
});
const columns = [
  { 
      id: 'type', 
      label: 'Type ', 
      minWidth: 60 
  },
  {
      id: 'amount',
      label: 'Amount',
      minWidth: 50,
  },
  {
      id: 'fee',
      label: 'Fee',
      minWidth: 50,
  },
  {
      id: 'currency',
      label: 'currency',
      minWidth: 50,
  },
  {
      id: 'running_balance',
      label: 'Running Balance',
      minWidth: 50,
  },
  {
      id: 'reference',
      label: 'Reference',
      minWidth: 80,
  },
  {
      id: 'status',
      label: 'Status',
      minWidth: 50,
  },
  {
      id: 'message',
      label: 'Message/Reason',
      minWidth: 80,
  },
  {
      id: 'created_at',
      label: 'Created at',
      minWidth: 50,
  },
  {
      id: 'actions',
      label: 'Actions',
      minWidth: 40,
      align: 'right',
  },
];

const TransactionsForm = ({ userId }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [transactionHistory, setTransactionHistory] = React.useState([]);
  const [accountBalances, setAccountBalances] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [inprogress, setInprogress] = React.useState(false);
  const [currentTransaction, setCurrentTransaction] = React.useState(null);
  const txFormUpdateRef = React.useRef(null);
  const modalViewRef = React.useRef(null);

  const handleView = (data) => {
      modalViewRef.current.open();
      setCurrentTransaction(data);
  };

  const closeViewOrder = () => {
      modalViewRef.current.close();
  };

  const handleSubmit = (values) => {
    setIsLoading(true);
    console.log(`Submitting the following data ${JSON.stringify(values)}`);
    getTransactionInfo(values).then(res => {
      if(res.status_code == 200 || res.status_code == 201) {
        setMessage(`${res.message}.`);
        setTransactionHistory(res.data);
      }else {
        toast(`Sorry! ${res.message}`);
        setErrorMessage(res.message);
      }
    }).catch(err => {
      toast(`Sorry, ${err.message}`)
      setErrorMessage(err.message);
    }).finally( () => {
      setIsLoading(false);
      setFormSubmitted(true);
    });
    getBalanceInfo(values).then(res => {
      if(res.status_code == 200 || res.status_code == 201) {
        setMessage(`${res.message}.`);
        setAccountBalances(res.data);
      }else {
        toast(`Sorry! ${res.message}`);
        setErrorMessage(res.message);
      }
    }).catch(err => {
      toast(`Sorry, ${err.message}`)
      setErrorMessage(err.message);
    }).finally( () => {
      setIsLoading(false);
      setFormSubmitted(true);
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  let exportData = (_type) => {
      let tab = transactionHistory.map((item) => {
        let obj = {
          id: item.id ?? item.transaction_id,
          external_ref: item.ext_transaction_id ?? '',
          type: item.type.split("_").join(" "),
          amount: item.amount,
          fee: item.fee ?? 0,
          email: item.currency,
          phone_number: item?.phone_number,
          refdata: item.phone_number ?? item.request_data ?? '',
          status: item.status,
          message: item.message,
          created_at: `${new Date(item.created_at).toDateString()}, ${new Date(item.created_at).toLocaleTimeString()}`,
          updated_at: new Date(item.updated_at).toDateString(),
        };
  
        return obj;
      });
      if (_type === "csv") Excel.writeFile(tab, "Transaction Report", "csv");
      else Excel.writeFile(tab, "txReport");
  };

  const closingBalance = accountBalances.reduce((acc, balance) => acc + parseFloat(balance.amount), 0);
  const transactionBalance = transactionHistory.reduce((acc, tx) => {
    if(tx.type == 'DIRECT_PAY') {
      return acc + parseFloat(tx.amount);
    }else if(tx.type == 'DIRECT_TRANSFER') {
      return acc - parseFloat(tx.amount);
    }else {
      return acc;
    }
  }, 0);

  const openingBalance = useMemo(() => {
    return closingBalance - transactionBalance;
  }, [closingBalance, transactionBalance]);

  return  (
    <div className="login-form">
      <div className="login-error">{ errorMessage ? errorMessage : ''}</div>
      <div className='login-info'><h2>AFTA WALLET</h2></div>
      { !formSubmitted && <div><h3>Enter the period of the transactions you want to get</h3></div> }
      { formSubmitted && <div><h3>Transaction History</h3></div> }
      { !formSubmitted &&
        <Formik
        initialValues={{
          pin: "",
          start: "",
          end: "",
          id: userId,
        }}
        onSubmit={(values) => {
          handleSubmit(values);
        }}
        validationSchema={orderSchema}>
        {({ handleSubmit, values, handleChange, errors, touched }) => (
          <form onSubmit={handleSubmit}>
      
            <div className={`field`}>
              <label htmlFor="start">Start Date</label>
              <input
                name="start"
                id="start"
                type='date'
                placeholder="History start date"
                value={values.start}
                onChange={handleChange}
              />
              {errors.start && touched.start ? (
                <div className="error">{errors.start}</div>
              ) : null}
            </div>
            <div className={`field`}>
              <label htmlFor="end">End date</label>
              <input
                name="end"
                id="end"
                type='date'
                placeholder="History End date"
                value={values.end}
                onChange={handleChange}
              />
              {
              errors.end && touched.end ? (
                <div className="error">{errors.end}</div>
              ) : null
              }
            </div>
            <div className={`field`}>
              <label htmlFor="pin">PIN code</label>
              <input
                name="pin"
                id="pin"
                type='password'
                placeholder="Votre code PIN"
                value={values.pin}
                onChange={handleChange}
              />
              {errors.pin && touched.pin ? (
                <div className="error">{errors.pin}</div>
              ) : null}
            </div>
            <button type="submit" disabled={isLoading}>
              {!isLoading && <span>CONFIRMER</span>}
              {/* {isLoading && <span>Sending ...</span>} */}
              <BeatLoader
                color={Constant.spinner_color}
                loading={isLoading}
                cssOverride={{
                  textAlign: 'center',
                  marginTop: '10px'
                }}
              />
              {/* {isLoading && <Loader />} */}
            </button>
          </form>
        )}
        </Formik>
      }
      {
        formSubmitted && <div>
          <h2>Account Balances</h2>
          <div className="grid lg:grid-cols-12 md:grid-cols-6 sm:grid-cols-8 gap-6 mt-4">
            {accountBalances
              .map((balance, index) => {
                return (
                  <div key={index} className="bg-[#fff] px-4 py-4 col-span-3 h-40 rounded-[10px]">
                    <div className="text-center">
                      <div className="col-span-2">
                        <p className="mb-2 text-[40px] font-bold __ellipsis">{Number(balance.amount).toFixed(2)}</p>
                        <h1 className="text-[30px] ">{balance.currency}</h1>
                      </div>
                    </div>
                  </div>
               )}
               )
            }
          </div>
          <div className="grid lg:grid-cols-12 md:grid-cols-6 sm:grid-cols-8 gap-6 mt-4">
            <TableButton
              text="CSV"
              color="#45B4CB"
              onClick={() => exportData("csv")}
            />
            <TableButton
              text="Excel"
              color="#58BC6E"
              onClick={() => exportData()}
            />
            <TableButton
              text="PDF"
              color="#d05372ff"
              onClick={() => window.print()}
            />
          </div>
          <Paper sx={{ width: '100%', overflow: 'scroll' }}>
            
                        <TableContainer style={{ boxShadow: 'none'}}>
                            <Table stickyHeader aria-label="sticky table">
                                <TableHead>
                                    <TableRow>
                                        {columns.map((column, index) => (
                                            <TableCell
                                                key={column.id}
                                                align="left"
                                                style={{ minWidth: column.minWidth }}>
                                                {column.label}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                  {/* <TableRow hover tabIndex={-1} key={'index'}>
                                    <>
                                        <TableCell align="left">
                                            Opening Balance
                                        </TableCell>
                                        <TableCell align="left">
                                            {openingBalance.toFixed(2)}
                                        </TableCell>
                                        <TableCell align="left">
                                        </TableCell>
                                        <TableCell align="left">
                                        </TableCell>
                                        <TableCell align="left">
                                        </TableCell>
                                        <TableCell align="left">
                                        </TableCell>
                                        <TableCell align="left">
                                        </TableCell>
                                        <TableCell align="left">
                                        </TableCell>
                                        <TableCell align="center">
                                        </TableCell>
                                    </>
                                  </TableRow> */}
                                    {transactionHistory
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((tx, index) => {
                                            return (
                                                <TableRow hover role="checkbox" tabIndex={-1} key={index}>
                                                    <>
                                                        <TableCell align="left">
                                                            {tx.type.split("_").join(" ")}
                                                        </TableCell>
                                                        <TableCell align="left">
                                                            {tx.amount}
                                                        </TableCell>
                                                        <TableCell align="left">
                                                            {tx.fee ?? 0}
                                                        </TableCell>
                                                        <TableCell align="left">
                                                            {tx.currency}
                                                        </TableCell>
                                                        <TableCell align="left">
                                                            {tx.running_balance ?? 0}
                                                        </TableCell>
                                                        <TableCell align="left">
                                                            {tx.phone_number ?? tx.request_data ?? ''}
                                                        </TableCell>
                                                        <TableCell align="left">
                                                            {tx.status}
                                                        </TableCell>
                                                        <TableCell align="left">
                                                            {tx.message}
                                                        </TableCell>
                                                        <TableCell align="left">
                                                            {new Date(tx.created_at).toDateString()}, {new Date(tx.created_at).toLocaleTimeString()}
                                                        </TableCell>
                                                        <TableCell align="center" key={index}>
                                                            <div className="flex gap-2">
                                                                <button onClick={() => handleView(tx)} className="bg-[#2DE2EE] rounded-full text-[#fff] p-2" tooltip = "View tx">
                                                                    <FaRegEye size={16} color="#000"/>
                                                                </button>
                                                            </div>
                                                        </TableCell>
                                                    </>
                                                </TableRow>
                                            );
                                        })}
                                  {/* <TableRow hover tabIndex={-1} key={'index'}>
                                    <>
                                        <TableCell align="left">
                                            Closing Balance
                                        </TableCell>
                                        <TableCell align="left">
                                            {closingBalance.toFixed(2)}
                                        </TableCell>
                                        <TableCell align="left">
                                        </TableCell>
                                        <TableCell align="left">
                                        </TableCell>
                                        <TableCell align="left">
                                        </TableCell>
                                        <TableCell align="left">
                                        </TableCell>
                                        <TableCell align="left">
                                        </TableCell>
                                        <TableCell align="left">
                                        </TableCell>
                                        <TableCell align="center">
                                                            
                                        </TableCell>
                                    </>
                                  </TableRow> */}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <BeatLoader
                            color={Constant.spinner_color}
                            loading={inprogress}
                            cssOverride={{
                                textAlign: 'center',
                                marginTop: '20px'
                            }}
                        />
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 1000]}
                            component="div"
                            count={transactionHistory.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
          </Paper>
          <Modal
            title="Transaction Info"
            onSubmit={()=>closeViewOrder()}
            submitBtn="Close"
            onClose={()=>console.log("")}
            ref={modalViewRef}>
              <ViewTxForm
                ref={txFormUpdateRef}
                data={currentTransaction}/>
          </Modal>
        </div>
      }
    </div>
  );
};

export default TransactionsForm;
